<template>
	<input
		:placeholder="placeholder"
		ref="numeric"
		type="tel"
		v-model="amount"
		v-if="!readOnly"
		v-on="inputListeners"
	>
	<span
		v-else
		ref="readOnly"
	>{{ amount }}</span>
</template>

<script>
//import accounting from "accounting-js";
import * as accounting from "@/components/custom-accounting-js/lib/index";

export default {
	name: "CustomVueNumeric",

	props: {
		/**
		 * Currency symbol.
		 */
		currency: {
			type: String,
			default: "",
			required: false,
		},

		/**
		 * Maximum value allowed.
		 */
		max: {
			type: Number,
			default: Number.MAX_SAFE_INTEGER || 9007199254740991,
			required: false,
		},

		/**
		 * Minimum value allowed.
		 */
		min: {
			type: Number,
			default: Number.MIN_SAFE_INTEGER || -9007199254740991,
			required: false,
		},

		/**
		 * Enable/Disable minus value.
		 */
		minus: {
			type: Boolean,
			default: false,
			required: false,
		},

		/**
		 * Input placeholder.
		 */
		placeholder: {
			type: String,
			default: "",
			required: false,
		},

		/**
		 * Value when the input is empty
		 */
		emptyValue: {
			type: [Number, String],
			default: "",
			required: false,
		},

		/**
		 * Value when the input is empty
		 */
		emptyAsNull: {
			type: Boolean,
			default: false,
			required: false,
		},

		/**
		 * Round value or not
		 */
		roundValue: {
			type: Boolean,
			default: true,
			required: false,
		},

		/**
		 * Number of decimals.
		 * Decimals symbol are the opposite of separator symbol.
		 */
		precision: {
			type: Number,
			default: 0,
			required: false,
		},

		/**
		 * Thousand separator type.
		 * Separator props accept either . or , (default).
		 */
		separator: {
			type: String,
			default: ",",
			required: false,
		},

		/**
		 * Forced thousand separator.
		 * Accepts any string.
		 */
		thousandSeparator: {
			default: undefined,
			required: false,
			type: String,
		},

		/**
		 * Forced decimal separator.
		 * Accepts any string.
		 */
		decimalSeparator: {
			default: undefined,
			required: false,
			type: String,
		},

		/**
		 * The output type used for v-model.
		 * It can either be String or Number (default).
		 */
		outputType: {
			required: false,
			type: String,
			default: "Number",
		},

		/**
		 * v-model value.
		 */
		value: {
			type: [Number, String],
			default: 0,
			required: true,
		},

		/**
		 * Hide input and show value in text only.
		 */
		readOnly: {
			type: Boolean,
			default: false,
			required: false,
		},

		/**
		 * Class for the span tag when readOnly props is true.
		 */
		readOnlyClass: {
			type: String,
			default: "",
			required: false,
		},

		/**
		 * Position of currency symbol
		 * Symbol position props accept either 'suffix' or 'prefix' (default).
		 */
		currencySymbolPosition: {
			type: String,
			default: "prefix",
			required: false,
		},
	},

	data: () => ({
		amount: "",
	}),

	computed: {
		/**
		 * Number type of formatted value.
		 * @return {Number}
		 */
		amountNumber() {
			//console.log("amountNumberComputed", this.amount, typeof this.amount);
			if (this.emptyAsNull === true && this.amount === "" && typeof this.amount == "string") {
				return null;
			}
			//console.log("amountNumberComputed UNFORMAT", this.amount);
			var res = this.unformat(this.amount);
			//console.log("amountNumberComputed UNFORMAT res", res);
			return res;
		},

		/**
		 * Number type of value props.
		 * @return {Number}
		 */
		valueNumber() {
			//console.log("valueNumberComputed", this.value, typeof this.value);
			if (this.emptyAsNull === true && this.value === "" && typeof this.value == "string") {
				return null;
			}
			return this.unformat(this.value);
		},

		/**
		 * Define listeners to attach to the input
		 */
		inputListeners() {
			var vm = this;
			return Object.assign({}, this.$listeners, {
				blur: vm.onBlurHandler,
				input: vm.onInputHandler,
				focus: vm.onFocusHandler,
			});
		},

		/**
		 * Define decimal separator based on separator props.
		 * @return {String} '.' or ','
		 */
		decimalSeparatorSymbol() {
			if (typeof this.decimalSeparator !== "undefined") return this.decimalSeparator;
			if (this.separator === ",") return ".";
			return ",";
		},

		/**
		 * Define thousand separator based on separator props.
		 * @return {String} '.' or ','
		 */
		thousandSeparatorSymbol() {
			if (typeof this.thousandSeparator !== "undefined") return this.thousandSeparator;
			if (this.separator === ".") return ".";
			if (this.separator === "space") return " ";
			return ",";
		},

		/**
		 * Define format position for currency symbol and value.
		 * @return {String} format
		 */
		symbolPosition() {
			if (!this.currency) return "%v";
			return this.currencySymbolPosition === "suffix" ? "%v %s" : "%s %v";
		},
	},

	watch: {
		/**
		 * Watch for value change from other input with same v-model.
		 * @param {Number} newValue
		 */
		valueNumber(newValue) {
			if (this.$refs.numeric !== document.activeElement) {
				//console.log("valueNumber watch", newValue);
				this.amount = this.format(newValue);
			}
		},

		/**
		 * When readOnly is true, replace the span tag class.
		 * @param {Boolean} newValue
		 * @param {Boolean} oldValue
		 */
		readOnly(newValue, oldValue) {
			if (oldValue === false && newValue === true) {
				this.$nextTick(() => {
					this.$refs.readOnly.className = this.readOnlyClass;
				});
			}
		},

		/**
		 * Immediately reflect separator changes
		 */
		separator() {
			this.process(this.valueNumber);
			//console.log("separator watch", this.valueNumber);
			this.amount = this.format(this.valueNumber);
		},

		/**
		 * Immediately reflect currency changes
		 */
		currency() {
			this.process(this.valueNumber);
			//console.log("currency watch", this.valueNumber);
			this.amount = this.format(this.valueNumber);
		},

		/**
		 * Immediately reflect precision changes
		 */
		precision() {
			this.process(this.valueNumber);
			//console.log("precision watch", this.valueNumber);
			this.amount = this.format(this.valueNumber);
		},
	},

	mounted() {
		//console.log(accounting);
		/*accounting.toFixed = (value, precision) => {
			if (this.roundValue) {
				console.log("accounting.toFixed ORIGINAL");
				return accountingToFixed(value, precision);
			}

			console.log("accounting.toFixed CUSTOM");
			precision = Math.round(Math.abs(precision));
			precision = isNaN(precision) ? accounting.settings.precision : precision;

			var exponentialForm = Number(accounting.unformat(value) + "e" + precision);
			var rounded = Math.round(exponentialForm);
			var finalResult = Number(rounded + "e-" + precision).toFixed(precision);
			console.log("finalResult", finalResult);
			return finalResult;
		};*/

		/*window.accounting = accounting;

		window._checkPrecisionFelipe = function(val, base) {
			val = Math.round(Math.abs(val));
			return isNaN(val) ? base : val;
		};
		window.toFixedFelipe = function(value, precision) {
			precision = window._checkPrecisionFelipe(precision, 2);
			console.log("prec", precision);
			var power = Math.pow(10, precision);
			console.log("power", power);
			console.log(
				"v",
				value + 1e-8,
				Number(value + "1e-" + precision),
				"pow",
				(value + 1e-8) * power,
				"rou",
				Math.round((value + 1e-8) * power),
				"div",
				Math.round((value + 1e-8) * power) / power
			);
			console.log(Number(value + "1e-" + precision), Number(value + "e" + precision), Number(value + "e-" + precision));

			var exponentialForm = Number(window.accounting.unformat(value) + "e" + precision);
			console.log("exponentialForm", exponentialForm);
			var rounded = Math.round(exponentialForm);
			console.log("rounded", rounded);
			var finalResult = Number(rounded + "e-" + precision).toFixed(precision);
			console.log("finalResult", finalResult);

			// Multiply up by precision, round accurately, then divide and use native toFixed():
			return (Math.round((value + 1e-8) * power) / power).toFixed(precision);
		};*/

		// Set default value props when placeholder undefined.
		if (!this.placeholder) {
			if (this.emptyAsNull !== true) {
				//console.log("MOUNTED NO PLACEHOLDER");
				this.process(this.valueNumber);
				this.amount = this.format(this.valueNumber);

				// In case of delayed props value.
				setTimeout(() => {
					this.process(this.valueNumber);
					this.amount = this.format(this.valueNumber);
				}, 500);
			}
		}

		// Set read-only span element's class
		if (this.readOnly) this.$refs.readOnly.className = this.readOnlyClass;
	},

	methods: {
		/**
		 * Handle blur event.
		 * @param {Object} e
		 */
		onBlurHandler(e) {
			//console.log("blur", this.valueNumber, typeof this.valueNumber, this.amount, typeof this.amount);
			this.$emit("blur", e);
			if (this.emptyAsNull === true && this.valueNumber === null) {
				//console.log("amountNull");
				this.amount = null;
			} else {
				//console.log("blur amount set", this.valueNumber);
				this.amount = this.format(this.valueNumber);
				//console.log("blur amount", this.amount);
			}
		},

		/**
		 * Handle focus event.
		 * @param {Object} e
		 */
		onFocusHandler(e) {
			this.$emit("focus", e);
			//console.log("FOCUS", this.valueNumber, typeof this.valueNumber, this.amount, typeof this.amount);
			if (this.valueNumber === 0 || (this.emptyAsNull === true && this.valueNumber === null)) {
				//console.log("focus amountNull");
				this.amount = null;
			} else {
				//console.log("FOCUS", this.valueNumber);
				this.amount = accounting.formatMoney(this.valueNumber, {
					symbol: "",
					format: "%v",
					thousand: "",
					decimal: this.decimalSeparatorSymbol,
					precision: Number(this.precision),
					roundValue: this.roundValue,
				});
			}
		},

		/**
		 * Handle input event.
		 */
		onInputHandler() {
			//console.log("customonInputHandler", this.amountNumber, typeof this.amountNumber, this.value, typeof this.value);
			if (this.emptyAsNull === true && this.amountNumber === null) {
				this.$emit("input", null);
				return;
			}
			//console.log("customonInputHandler Process", this.amountNumber, typeof this.amountNumber, this.value, typeof this.value);
			this.process(this.amountNumber);
		},

		/**
		 * Validate value before update the component.
		 * @param {Number} value
		 */
		process(value) {
			//console.log("PROCESS", value, typeof value);
			if (this.emptyAsNull === true && ((value === null && typeof value == "object") || (value === "" && typeof value == "string"))) {
				this.$emit("input", null);
				return;
			}
			if (value >= this.max) this.update(this.max);
			if (value <= this.min) this.update(this.min);
			if (value > this.min && value < this.max) this.update(value);
			if (!this.minus && value < 0) this.min >= 0 ? this.update(this.min) : this.update(0);
		},

		/**
		 * Update parent component model value.
		 * @param {Number} value
		 */
		update(value) {
			//console.log("UPDATE", value, typeof value, this.decimalSeparatorSymbol, this.thousandSeparatorSymbol);
			var fixedValue = null;
			if (this.roundValue || !this.precision) {
				//console.log("UPDATE round", value);
				fixedValue = accounting.toFixed(value, this.precision, this.roundValue);
			} else {
				//console.log("UPDATE NO ROUND", value);
				//var with2Decimals = num.toString().match(/^-?\d+(?:\.\d{0,2})?/)[0]
				//var decimals = value.toString().substring(value.toString().indexOf(".") + 1);
				var decimals = value.toString().substring(value.toString().indexOf("."));
				var temp = value.toString().replace(decimals, "");
				decimals = decimals.toString().replace(".", "");
				//console.log("update int", temp, "decimals", decimals);
				if (decimals.toString().trim().length > 0 && temp.toString().trim().length > 0) {
					//console.log("update ok");
					fixedValue =
						temp.toString() +
						"." +
						(decimals.toString().length > this.precision ? decimals.toString().substring(0, this.precision) : decimals.toString());
					//console.log("fixed", fixedValue);
					//fixedValue = accounting.toFixed(Number(fixedValue), this.precision);
					//fixedValue = Number(fixedValue);
				} else {
					//console.log("update fiexed", value);
					fixedValue = accounting.toFixed(value, this.precision, this.roundValue);
					//console.log("update fiexedvalue", fixedValue);
				}
			}
			const output = this.outputType.toLowerCase() === "string" ? fixedValue : Number(fixedValue);
			//console.log("output", output);
			this.$emit("input", output);
		},

		/**
		 * Format value using symbol and separator.
		 * @param {Number} value
		 * @return {String}
		 */
		format(value) {
			//console.log("FORMAT", value, typeof value);
			if (this.emptyAsNull === true && ((value === null && typeof value == "object") || (value === "" && typeof value == "string"))) {
				return null;
			}
			//console.log("FORMAT", value, typeof value);
			var res = accounting.formatMoney(value, {
				symbol: this.currency,
				format: this.symbolPosition,
				precision: Number(this.precision),
				decimal: this.decimalSeparatorSymbol,
				thousand: this.thousandSeparatorSymbol,
				roundValue: this.roundValue,
			});
			//console.log("FORMAT res", res);
			return res;
		},

		/**
		 * Remove symbol and separator.
		 * @param {Number} value
		 * @return {Number}
		 */
		unformat(value) {
			//console.log("UNFORMAT", value, typeof value, this.emptyAsNull);
			if (this.emptyAsNull === true && ((value === null && typeof value == "object") || (value === "" && typeof value == "string"))) {
				return null;
			}
			var toUnformat = typeof value === "string" && value === "" ? this.emptyValue : value;
			if (!this.roundValue && this.precision && typeof toUnformat === "string" && this.decimalSeparatorSymbol) {
				//console.log("TOUNFORMAT", toUnformat);
				//var decimals = toUnformat.toString().substring(toUnformat.toString().indexOf(this.decimalSeparatorSymbol) + 1);
				var decimals = toUnformat.toString().substring(toUnformat.toString().indexOf(this.decimalSeparatorSymbol));
				var temp = toUnformat.toString().replace(decimals, "");
				decimals = decimals.toString().replace(this.decimalSeparatorSymbol, "");
				//console.log("int", temp, "decimals", decimals);
				if (decimals.toString().trim().length > 0 && temp.toString().trim().length > 0) {
					toUnformat =
						temp.toString() +
						this.decimalSeparatorSymbol +
						(decimals.toString().length > this.precision ? decimals.toString().substring(0, this.precision) : decimals.toString());
					//console.log("fixed", toUnformat);
					//fixedValue = accounting.toFixed(Number(fixedValue), this.precision);
					//fixedValue = Number(fixedValue);
				}
			}
			var res = accounting.unformat(toUnformat, this.decimalSeparatorSymbol);
			//console.log("TO UNFORMAT res", toUnformat, typeof toUnformat, this.decimalSeparatorSymbol, res);
			return res;
		},
	},
};
</script>
